/**
 * Endpoints whose API errors should not raise a toast.
 *
 * This is a temporary muzzle, not a fix. Several endpoints answer a perfectly normal
 * "nothing here" with a 404, so ErrorInterceptor shows the user an error for something that
 * is not one. Until those responses are corrected server-side, listing an endpoint here stops
 * the toast.
 *
 * What suppression does NOT change: the request still fails, and the error is rethrown to the
 * caller exactly as before. Component-level `catch` blocks, loaders and inline messages keep
 * working - only the global toast is skipped. So this is safe to apply to an endpoint whose
 * failure the page already handles itself, and wrong for one where the toast is the user's
 * only signal.
 */

/**
 * A URL fragment, matched case-insensitively anywhere in the request URL. The path is enough:
 *
 *     'api/master/Vessel/getvesseldetail'
 *
 * A URL pasted straight from the browser's network tab also works - the host and query string
 * are stripped before matching, so it stays correct across environments and query values:
 *
 *     'https://perform.geoserves-test.com/api/ets/UserRoles/GetUserRolesDataByUserId?userId=937'
 *
 * Use a RegExp when the path carries an id in the middle, or to anchor the match:
 *
 *     /api\/voyage\/Ems\/\d+\/summary$/i
 *
 * Keep it as specific as the endpoint you mean - a fragment like 'api/master' would silence
 * a whole service.
 */
export type SuppressedApiUrl = string | RegExp;

/**
 * The long form, for when a bare URL is not enough.
 *
 * `reason` and `ticket` are documentation only - nothing renders them. They are here so the
 * next person reading this list can tell a deliberate workaround from an entry nobody
 * remembers adding, and so a fixed endpoint has an obvious reason to be deleted.
 */
export interface SuppressedApiErrorRule {
    url: SuppressedApiUrl;

    /** Status codes to suppress. Defaults to 404 - widen it only when you mean to. */
    status?: number | number[];

    /** Why this endpoint is here - what it returns and why that is not a real error. */
    reason?: string;

    /** Ticket tracking the server-side fix. */
    ticket?: string;
}

export type SuppressedApiError = SuppressedApiUrl | SuppressedApiErrorRule;

/**
 * Add endpoints here. A bare URL suppresses its 404s, which is the usual case:
 *
 *     'api/master/Vessel/getvesseldetail',
 *
 * Use the object form only to suppress something other than a 404, or to leave a note:
 *
 *     { url: 'api/report/Legacy/pull', status: [404, 500], ticket: 'GPR-0000' },
 */
export const SUPPRESSED_API_ERRORS: SuppressedApiError[] = [
    {
        url: '/api/ets/UserRoles/GetUserRolesDataByUserId',
        status: 400,
        reason: 'Reports "this user has no ETS role" as 400 "no Data found", which is a '
            + 'normal state for a user with ETS access but no role row - not an error.',
    },
    {
        url: '/api/voyage/Voyages/getcargocompositionbyvoyage',
        status: 404,
        reason: 'Returns 404 rather than an empty list for a voyage with no cargo '
            + 'composition recorded, which is normal for most voyages.',
    },
    {
        url: '/api/voyage/Voyages/getAllVoyagesByFilter',
        status: 500,
        reason: 'Server-side fault, cause not yet identified - the only entry here that hides '
            + 'a genuine 500 rather than a misused status code. getAndSetVoyageList() already '
            + 'catches the rejection and shows its own message, so the global toast was the '
            + 'second of two. Remove this as soon as the API is fixed: while it is here, a new '
            + 'fault on this endpoint is invisible.',
    },
    // ── Technical tab: three endpoints, one defect ───────────────────────────────
    // All three read through a per-vessel query-cache loader in gp-AnalyticsService that
    // deserialized its cached value unguarded, so an unreadable entry threw a JsonException
    // and surfaced as a 500. The sliding TTL is refreshed on read before the value is parsed,
    // so a bad key was renewed by every failed request and never expired on its own - which is
    // why these did not recover. Fixed there by GetJsonAsync
    // (Abstractions/Cache/DistributedChartCacheJsonExtensions.cs).
    //
    // Safe to silence in the meantime: loadTechnicalData wraps every analytics call in
    // catchError(() => of(null)) and renders that chart as "no data", so the toast was the only
    // thing contradicting a deliberate fallback. The other three technical calls stay loud.
    //
    // Remove all three together once that build reaches this environment and the charts render.
    { url: '/api/analytics-service/analytics/power-rpm/',          status: 500 },
    { url: '/api/analytics-service/analytics/main-engine-health/', status: 500 },
    { url: '/api/analytics-service/analytics/load-sfoc/',          status: 500 },
];

/** True when this request's failure should be swallowed silently by ErrorInterceptor. */
export function isSuppressedApiError(url: string, status: number): boolean {
    const path = toMatchablePath(url);
    if (!path) {
        return false;
    }
    const lowerPath = path.toLowerCase();

    return SUPPRESSED_API_ERRORS.map(toRule).some(
        rule => matchesStatus(rule, status) && matchesUrl(rule, path, lowerPath),
    );
}

/** Widens the shorthand forms to the object the matchers expect. */
function toRule(entry: SuppressedApiError): SuppressedApiErrorRule {
    return typeof entry === 'string' || entry instanceof RegExp ? { url: entry } : entry;
}

function matchesStatus(rule: SuppressedApiErrorRule, status: number): boolean {
    const statuses = rule.status ?? 404;
    return Array.isArray(statuses) ? statuses.includes(status) : statuses === status;
}

function matchesUrl(rule: SuppressedApiErrorRule, path: string, lowerPath: string): boolean {
    if (rule.url instanceof RegExp) {
        // A /g pattern keeps lastIndex between calls, so an unreset one would match only
        // every other request.
        rule.url.lastIndex = 0;
        return rule.url.test(path);
    }

    const fragment = toFragment(rule.url).toLowerCase();
    // An empty fragment would match every URL and silence the whole app.
    return !!fragment && lowerPath.includes(fragment);
}

/**
 * Normalises a string rule so a URL copied out of the browser's network tab works as-is.
 *
 * Drops the query string - the request URL is matched with its own query removed, so a rule
 * that kept `?userId=937` could never match, and would pin the rule to one user's id anyway.
 *
 * Drops the scheme and host too: those differ per environment, so a rule copied from the test
 * host would silently stop matching on dev and prod.
 */
function toFragment(url: string): string {
    return toMatchablePath(url).replace(/^[a-z][a-z0-9+.-]*:\/\/[^/]*/i, '');
}

/** The request URL without its query string or fragment. Host is left in place. */
function toMatchablePath(url: string): string {
    if (typeof url !== 'string') {
        return '';
    }
    return url.split('#')[0].split('?')[0].trim();
}
